import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import NoticeSliderAPI from '../components/layout/NoticeSliderAPI';
import GamePopup from '../components/common/GamePopup';
import ProviderGrid from '../components/common/ProviderGrid';
import SearchBox from '../components/common/SearchBox';
import gamesService, { gameListErrorMessage } from '../services/games';
import GameListStatus from '../components/common/GameListStatus';
import { getCustomGradient } from '../utils/gameUtils';
import { getProviderLogoPath, resolveApiImageUrl } from '../utils/logoUtils';
import useModal from '../hooks/useModal';
import Modal from '../components/common/Modal';
import './SlotPage.css';

/**
 * 루미나 화면 층 신작 — 블랙 `pages/SlotPage.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `sl-` 슬롯 + 템플릿 `gl-` 게임 리스트 팝업).
 * WHY: SPEC §2 — 게임사 목록(노출·선택 게이트, 라이브 전용 8코드 제외), 게임사 클릭 = 게임 목록 팝업(로비 직진입 비활성 2026-06-03 그대로),
 *      게임 클릭 = 실행 팝업(GamePopup), 종료·이탈·탭 닫기 신호(endGame / sendBeacon), 실패 사유 공용 모달은 원본 훅·핸들러가 그대로 담당한다.
 *      화면은 제목 블록(검색 + 홈으로) → 공지 슬라이더 → 게임사 5열 카드(ProviderGrid) → 게임 리스트 팝업(.gl-*) 순으로 그린다.
 *      제목 블록의 검색은 블랙 게임 실행 화면이 쓰던 SearchBox(게임 검색 API·0.2초 디바운스)를 그대로 두고, 고른 게임은 이 화면의 실행 흐름(handleGameClick)으로 보낸다.
 */
const SlotPage = () => {
  const [providers, setProviders] = useState([]);
  const [popularGames, setPopularGames] = useState([]);
  const [newGames, setNewGames] = useState([]);
  const [recommendedGames, setRecommendedGames] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerGames, setProviderGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showProviderPopup, setShowProviderPopup] = useState(false);
  const [gameListLoading, setGameListLoading] = useState(false);
  const [gameListError, setGameListError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isMobileIOS, setIsMobileIOS] = useState(false);

  // 게임 팝업 상태
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [gameUrl, setGameUrl] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameLoading, setGameLoading] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  // WHY: 게임/로비 실행 실패 사유를 네이티브 alert 대신 공용 정적 모달로 노출(화면전환에 묻혀 사라지는 문제 해소)
  const { isOpen: isAlertOpen, modalConfig: alertConfig, showAlert, closeModal: closeAlert } = useModal();

  // [2026-06-02] 슬롯 로비 직진입용으로 만든 팝업 창/감시 ref. 2026-06-03 슬롯 로비 직진입을 끈 뒤로
  //   감시 함수(startLobbyPopupMonitor)를 부르는 곳이 없어 아래 ref 는 항상 비어 있다(2026-09-29 실측).
  const lobbyPopupRef = useRef(null);
  const lobbyCheckIntervalRef = useRef(null);

  /* [2026-08-12] 게임 창이 열려 있는지를 ref 로도 들고 있는다.
     WHY: 아래 화면 이탈 정리는 한 번만 등록되는 처리라, 그 안에서 상태값을 읽으면
       화면에 처음 들어왔을 때의 값(닫힘)만 보인다. 이탈하는 순간의 실제 값이 필요하므로
       상태가 바뀔 때마다 이 ref 에 옮겨 둔다. */
  const gamePopupOpenRef = useRef(false);

  const handleLobbyPopupClose = useCallback(() => {
    // [2026-08-21] 게임 창이 실제로 닫힌 것을 확인한 신호 → 확정 종료.
    gamesService.endGame(true).catch(err => {
      console.error('[SlotPage] 로비 게임 종료 처리 실패:', err);
    });
    lobbyPopupRef.current = null;
  }, []);

  const startLobbyPopupMonitor = useCallback((popupWindow) => {
    if (lobbyCheckIntervalRef.current) clearInterval(lobbyCheckIntervalRef.current);
    lobbyPopupRef.current = popupWindow;
    lobbyCheckIntervalRef.current = setInterval(() => {
      if (popupWindow.closed) {
        clearInterval(lobbyCheckIntervalRef.current);
        lobbyCheckIntervalRef.current = null;
        handleLobbyPopupClose();
      }
    }, 1000);
  }, [handleLobbyPopupClose]);

  // 초기 데이터 로드
  useEffect(() => {
    loadInitialData();

    // iOS 모바일만 감지
    const checkMobileIOS = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      const isMobile = window.innerWidth <= 768;
      setIsMobileIOS(isIOS && isMobile); // iOS 모바일만
    };

    checkMobileIOS();
    window.addEventListener('resize', checkMobileIOS);

    // 컴포넌트 언마운트 시 body 스크롤 복원
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', checkMobileIOS);
      // [2026-06-02] 로비 팝업 감시 정리 + 열린 채 언마운트 시 게임 종료(세션 누수 방지)
      if (lobbyCheckIntervalRef.current) {
        clearInterval(lobbyCheckIntervalRef.current);
        lobbyCheckIntervalRef.current = null;
      }
      /* [2026-08-21] 게임 창이 살아 있는 채로 화면만 벗어나면 종료를 알리지 않는다.
         창이 닫히는 것은 위의 닫힘 감지가 잡는다(카지노와 같은 이유). */
      if (lobbyPopupRef.current && !lobbyPopupRef.current.closed) {
        console.log('[SlotPage] 화면 이탈 - 게임 창은 열려 있어 종료를 알리지 않음');
      }
      /* [2026-08-12] 게임 창을 켜 둔 채 화면을 벗어날 때도 종료를 알린다.
         WHY(실측): 종전 이 정리는 로비 창만 봤다. 목록에서 게임을 켠 뒤 다른 메뉴로 이동하면
           알려 줄 주체가 없어, 서버는 회원이 계속 게임 중인 것으로 알았다(회원관리 접속상태가
           '게임중'으로 잔류). 접속상태는 표시뿐 아니라 잔액 맞춤·관리자 작업을 막는 조건이기도 하다.
         닫기 버튼으로 닫는 경우는 종전대로 그쪽에서 알린다(중복 호출되지 않는다 — 닫으면 이 값이 꺼진다). */
      /* [2026-08-21] 화면 안 덧창은 화면과 함께 사라지므로 게임도 확실히 끝난다 → 확정 종료. */
      if (gamePopupOpenRef.current) {
        gamesService.endGame(true).catch(err => console.error('[SlotPage] 언마운트 시 게임 종료 실패:', err));
      }
    };
  }, []);

  /* [2026-08-12] 게임 창 열림 상태를 위 ref 에 계속 반영한다(화면 이탈 시점의 실제 값을 쓰기 위함). */
  useEffect(() => {
    gamePopupOpenRef.current = showGamePopup;
  }, [showGamePopup]);

  const loadInitialData = async () => {
    try {
      // [2026-06-24 성능] 이 화면에서 렌더하는 것은 슬롯 게임사 그리드(getProviders('slot')) 뿐이다.
      //   인기/신규/추천 게임은 슬롯 화면에서 렌더하지 않으므로(미사용 의존), 벤더카드 paint 를 막지 않도록
      //   loading 임계경로에서 분리한다 — providers 도착 즉시 loading 해제(카드 표시), 나머지는 비차단 로드.
      //   상태 세팅은 보존(향후 섹션 재도입 시 무손상)하되 await 체인에서 빼 카드 지연을 끊는다.
      const providersData = await gamesService.getProviders('slot');

      console.log('Providers data:', providersData);
      // 라이브 카지노 전용 프로바이더 제외
      const liveCasinoOnlyProviders = ['EVOLUTION', 'AG', 'DREAMGAME', 'EZUGI', 'MG', 'SEXY', 'VIVO', 'WM'];
      const filteredProviders = (providersData.providers || []).filter(provider => {
        const providerName = provider.provider || provider.provider_name || provider.name;
        return !liveCasinoOnlyProviders.includes(providerName?.toUpperCase());
      });
      setProviders(filteredProviders);

      // 비차단 보조 데이터: 카드 표시를 지연시키지 않도록 백그라운드로 로드하고 도착 시 상태만 갱신한다.
      gamesService.getPopularGames(20).then(d => setPopularGames(d.games || [])).catch(() => {});
      gamesService.getNewGames(20).then(d => setNewGames(d.games || [])).catch(() => {});
      gamesService.getRecommendedGames(20).then(d => setRecommendedGames(d.games || [])).catch(() => {});
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      // 에러 시 빈 배열로 명시적 설정
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  // 게임사 선택 시 게임 목록 로드
  // [2026-11-02] 마지막으로 누른 게임사만 창을 열고 실패를 안내한다. 응답이 막힌 동안 여러 번 누르면 안내가 반복되거나
  //   다른 게임사 목록 위에 떴다. 화면을 떠나면 번호를 올려 늦은 실패를 안내하지 않는다(2026-11-02 독립 검토).
  const providerClickSeqRef = useRef(0);
  useEffect(() => () => { providerClickSeqRef.current += 1; }, []);

  const handleProviderClick = async (provider) => {
    // 인증 체크
    if (!user) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    // [2026-06-03 슬롯 로비 직진입 비활성화] 로비 보유 게임사(has_lobby)를 '로비 직진입'시키면
    //   백엔드 카지노 로비 엔드포인트(/api/games/vendor/:vendor/lobby)를 호출하게 되어, 슬롯에서 눌러도
    //   그 게임사의 '카지노 로비'가 열리는 오접속이 발생한다(슬롯 전용 로비 경로가 없어 카지노 로비를 재사용했음).
    //   슬롯 화면은 항상 개별 게임목록을 띄우도록 환원한다(원래 안정 동작). 슬롯 전용 로비가 준비되면 그때 분기 복원.
    const seq = ++providerClickSeqRef.current;
    try {
      // providerCode 정규화
      // [2026-11-01] 띄어쓰기를 밑줄로 바꾸지 않는다 — 게임사 표준 코드에 띄어쓰기가 있는 곳(NOLIMIT CITY·PG SOFT)은 밑줄 표기로 물으면 서버가 게임 0개를
      //   돌려줘 게임 목록이 비었다(2026-11-01 ur3 실측: 노리밋시티 139·PG소프트 177 → 0). 서버는 대소문자를 무시하고 표준 코드와 그대로 맞춘다.
      const providerCode = (provider.provider || provider.provider_name || '')
        .toLowerCase();

      // [2026-11-03] 창을 먼저 열고 목록은 창 안에서 기다린다. WHY: 목록을 다 받은 뒤에 창을 열어, 응답이 밀리면 눌러도 몇 초~십수 초 아무 반응이 없었다(운영자 신고 "팝업이 늦게 뜨거나 뜨지 않는다").
      setSelectedProvider(provider);
      setProviderGames([]);
      setFilteredGames([]);
      setSearchKeyword('');
      setGameListError('');
      setGameListLoading(true);
      setShowProviderPopup(true);
      // 팝업 열 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
      const data = await gamesService.getGameList({ vendor: providerCode, game_type: 'slot' });
      if (seq !== providerClickSeqRef.current) return;   // 그 사이 다른 게임사를 눌렀거나 창을 닫았거나 화면을 떠났다
      const games = data.data || data.games || [];
      setProviderGames(games);
      setFilteredGames(games);
    } catch (error) {
      console.error('게임 목록 로드 실패:', error);
      // 창 안에 사유와 [다시 시도]를 보여 준다(알림창을 겹쳐 띄우지 않는다)
      if (seq !== providerClickSeqRef.current) return;
      setGameListError(gameListErrorMessage(error));
    } finally {
      if (seq === providerClickSeqRef.current) setGameListLoading(false);
    }
  };

  // 게임 실행
  const handleGameClick = async (game) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setGameLoading(true);
      setSelectedGame(game);

      // 게임 실행 URL 가져오기
      const launchData = await gamesService.launchGame(game.id);

      if (launchData && launchData.success && launchData.gameUrl) {
        setGameUrl(launchData.gameUrl);
        setShowGamePopup(true);
        // 게임 종료 후 게임리스트 팝업 유지를 위해 닫지 않음
      } else {
        // WHY: 2xx 응답이나 gameUrl 누락 등 비정상 — 본문 error 우선, 없으면 일반 문구. 공용 모달로 노출
        showAlert(launchData?.error || '게임을 실행할 수 없습니다.');
      }
    } catch (error) {
      // WHY: 백엔드(routes/games.js:2710 의 'API 잔액이 부족합니다. 관리자에게 문의하세요.' 등)가
      // 명확한 사유 메시지를 보내는데 일반 메시지로 덮으면 회원이 거부 사유를 알 수 없어
      // 운영자에게 알릴 수도 없다. services/games.js 가 axios 에러를
      // `throw error.response?.data || error` 로 던지므로 error 는 plain object({ error:'...' }) 또는 Error.
      console.error('게임 실행 실패:', error);
      // 안 읽은 쪽지 차단은 services/api.js 가 쪽지 안내 창을 연다 — 같은 내용의 알림창을 겹쳐 띄우지 않는다(2026-11-01 ur3 실측: 창 2개 겹침)
      if (error?.code === 'UNREAD_NOTICE_GATE') return;
      const reason = error?.error || error?.message || '게임을 실행할 수 없습니다.';
      // WHY: 사유를 네이티브 alert 대신 공용 모달로 노출(사라짐 해소)
      showAlert(reason);
    } finally {
      setGameLoading(false);
    }
  };


  /* [2026-08-21] 브라우저 탭을 닫을 때 게임 창 상태를 함께 알린다.
     WHY(실측): 종전에는 게임 창을 켜 둔 채 탭을 닫으면 아무 신호도 가지 않아, 회원이 게임을
       끝냈는지 이어 가는지 서버가 알 길이 없었다. 그런데 탭이 닫히는 순간 그 창이 이미 닫혀
       있었는지는 우리 화면이 알 수 있다.
     판정: 게임 창이 이미 닫혀 있으면 게임이 확실히 끝난 것이므로 확정 종료로 알린다.
       아직 열려 있으면 게임은 게임사 쪽에서 이어지므로 확정으로 보내지 않는다(베팅으로 판단).
     방식: 창이 닫히는 중에는 일반 요청이 취소될 수 있어 브라우저의 전송 보장 수단을 쓴다. */
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const popup = lobbyPopupRef.current;
      const overlayOpen = gamePopupOpenRef.current === true;   // 화면 안 덧창은 탭과 함께 사라진다
      if (!popup && !overlayOpen) return;                      // 게임을 켠 적이 없으면 알릴 것이 없다
      const alreadyClosed = overlayOpen ? true : (popup ? popup.closed === true : true);
      navigator.sendBeacon(
        '/api/games/end-session-beacon',
        JSON.stringify({ token, confirmed: alreadyClosed })
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const closeGamePopup = () => {
    setShowGamePopup(false);
    setGameUrl('');
    setSelectedGame(null);

    // [2026-08-21] 회원이 덧창을 직접 닫았다 → 게임이 확실히 끝난 신호(확정 종료).
    gamesService.endGame(true).catch(err => {
      console.error('게임 종료 처리 실패:', err);
    });
  };

  const closeProviderPopup = () => {
    providerClickSeqRef.current += 1;   // 창을 닫으면 늦게 온 목록·실패를 무시한다
    setGameListLoading(false);
    setGameListError('');
    setShowProviderPopup(false);
    setSelectedProvider(null);
    setProviderGames([]);
    setFilteredGames([]);
    setSearchKeyword('');
    // 팝업 닫을 때 body 스크롤 복원
    document.body.style.overflow = '';
  };

  /* [2026-10-31] 게임 목록 창을 ESC 로 닫는다 — 홈 화면(HomePage.jsx)의 같은 창과 같은 규칙(시안 게임 리스트 팝업: 닫기·배경 클릭·ESC).
     WHY: 이 화면만 ESC 처리가 빠져 있었다(2026-10-31 ur3 실측). 게임 실행 창(GamePopup)이 위에 떠 있으면 그 창의 ESC 처리에 맡겨
          두 창이 한꺼번에 닫히지 않게 한다. 알림창이 떠 있으면 알림창이 ESC 를 먼저 가져간다(utils/dialogKeyboard.js). */
  useEffect(() => {
    if (!showProviderPopup || showGamePopup) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeProviderPopup(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProviderPopup, showGamePopup]);

  // 게임 검색 처리
  const handleGameSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);

    if (!keyword.trim()) {
      setFilteredGames(providerGames);
      return;
    }

    const filtered = providerGames.filter(game =>
      (game.game_name_ko && game.game_name_ko.toLowerCase().includes(keyword.toLowerCase())) ||
      (game.game_name && game.game_name.toLowerCase().includes(keyword.toLowerCase()))
    );
    setFilteredGames(filtered);
  };

  /* [루미나] 썸네일 유무 판정 — 유저 API 가 썸네일이 없는 게임에 문자열 'null' 을 내려보내는 경우가 있어(실측) 그것도 없음으로 본다.
     없으면 시안 규칙(게임명 첫 글자 타일 .ph)으로 자리를 채운다. */
  const hasThumb = (url) => !!url && url !== 'null';
  const firstChar = (game) => ((game.game_name_ko || game.game_name || '?').trim().charAt(0) || '?');

  if (loading) {
    return (
      <section className="page page-app page-slot">
        <div className="container"><div className="lk-empty">로딩 중...</div></div>
      </section>
    );
  }

  return (
    <section className="page page-app page-slot">
      <div className="container">
        {/* 제목 블록 — 검색(SearchBox, 게임 선택 = 이 화면의 실행 흐름) + 홈으로 */}
        <div className="pg-head">
          <div><span className="eyebrow">SLOT</span><h2>슬롯</h2></div>
          <div className="acts">
            <div className="sl-tools">
              <SearchBox placeholder="게임 검색..." type="game" onSelect={handleGameClick} />
              <Link className="btn-secondary act" to="/">홈으로</Link>
            </div>
          </div>
        </div>

        {/* 공지사항 슬라이더 */}
        <NoticeSliderAPI />

        <div className="sl-meta"><span>게임사 <b>{providers.length}</b>곳</span></div>

        {/* 게임사 그리드 */}
        <ProviderGrid
          providers={providers}
          type="slot"
          onProviderClick={handleProviderClick}
        />
      </div>

      {/* 게임사별 게임 팝업(시안 게임 리스트 팝업)
          [2026-10-31] 페이지 맨 바깥(body)에 그린다 — 본문(main)의 겹침 층 1 안에서는 창이 상단 메뉴(60) 밑에 깔려, 데스크톱에서
          창 윗줄(게임사 이름·창 안 "게임 검색..."·닫기)이 보이지도 눌리지도 않았다(2026-10-31 ur3 실측, 1920·1440·1366 모두 0/9).
          'sl-games-ov' 는 이 창 전용 꾸밈(SlotPage.css)을 잇는 이름표 — 본문 밖으로 나오면 종전 '.page-slot' 묶음이 닿지 않는다. */}
      {showProviderPopup && selectedProvider && createPortal(
        <div className="gl-overlay sl-games-ov" onClick={closeProviderPopup}>
          <div className="gl-popup" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="gl-head">
              <img
                className="gl-chr"
                src={resolveApiImageUrl(selectedProvider.logo_url)}
                alt={selectedProvider.provider}
                loading="lazy"
                onError={(e) => {
                  if (!e.target.dataset.errorHandled) {
                    e.target.dataset.errorHandled = 'true';
                    if (!e.target.src.includes('default-logo.svg')) {
                      e.target.src = '/img/default-logo.svg';
                    } else {
                      e.target.style.display = 'none';
                    }
                  }
                }}
              />
              <div className="gl-tt">
                <h3>{selectedProvider.name_ko || selectedProvider.name || selectedProvider.provider}</h3>
                <span className="tag">{gameListLoading ? '불러오는 중' : `${providerGames.length.toLocaleString('ko-KR')} 게임`}</span>
              </div>
              <label className="gl-search">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
                <input
                  type="text"
                  placeholder="게임 검색..."
                  value={searchKeyword}
                  onChange={handleGameSearch}
                  disabled={gameListLoading}
                  autoComplete="off"
                  aria-label="게임 검색"
                />
              </label>
              <button className="gl-close" type="button" onClick={closeProviderPopup} aria-label="닫기">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>
            <div className="gl-grid">
              <GameListStatus className="gl-empty" loading={gameListLoading} error={gameListError} onRetry={() => handleProviderClick(selectedProvider)} />
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className={`gl-item ${isMobileIOS ? 'mobile-ios' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleGameClick(game)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGameClick(game); } }}
                  style={{
                    '--random-gradient': getCustomGradient(game.id, {
                      opacity: 0.9,
                      startPosition: 62,
                      endPosition: 95,
                      direction: 'to bottom'
                    })
                  }}
                >
                  <div className="gl-thumb">
                    {/* 자리표시(첫 글자)는 항상 깔고, 그림이 있으면 그 위에 얹는다 — 그림이 깨지면 자리표시가 그대로 보인다 */}
                    <span className="ph" aria-hidden="true">{firstChar(game)}</span>
                    {hasThumb(game.thumbnail_url) && (
                      isMobileIOS ? (
                        <div
                          className="gl-thumb-bg"
                          style={{ backgroundImage: `url(${game.thumbnail_url})` }}
                        />
                      ) : (
                        <img
                          src={game.thumbnail_url}
                          alt={game.game_name}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )
                    )}
                    {/* [2026-07-27] 게임사 표시를 '이름 글자'에서 '로고'로 바꾼다(슬라이드 카드와 동일 방식).
                        게임 그림 왼쪽 위에 로고를 얹는다. 로고는 관리자에 등록된 게임사 로고를 그대로 쓰며(게임사 카드와 같은 출처),
                        등록 로고가 없으면 기존 이름 규칙 경로로, 그마저 없으면 표시하지 않는다. */}
                    {(() => {
                      const logoSrc = game.vendor_logo
                        ? resolveApiImageUrl(game.vendor_logo)
                        : (() => {
                            const f = getProviderLogoPath(game.provider);
                            return f && !f.includes('default-logo') ? f : null;
                          })();
                      return logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={game.vendor_name_ko || game.provider || ''}
                          className="gl-vlogo"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : null;
                    })()}
                  </div>
                  <div className="gl-name">{game.game_name_ko || game.game_name}</div>
                </div>
              ))}
              {!gameListLoading && !gameListError && filteredGames.length === 0 && <div className="gl-empty">검색 결과가 없습니다</div>}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 게임 실행 팝업 */}
      {showGamePopup && (
        <GamePopup
          game={selectedGame}
          gameUrl={gameUrl}
          onClose={closeGamePopup}
          loading={gameLoading}
        />
      )}

      {/* WHY: 실패 사유 안내용 공용 모달 — createPortal(body)라 레이아웃·스크롤 무영향.
          id는 상단바(TopNavBar)의 'alertModal'과 충돌 방지를 위해 페이지 고유값 사용(스타일은 class 기반이라 영향 없음). */}
      <Modal id="slotAlertModal" isOpen={isAlertOpen} onClose={closeAlert} text={alertConfig.text} type={alertConfig.type} />
    </section>
  );
};

export default SlotPage;
